import React from "react";
import { formatDuration, formatSecondsBrief } from "../../../../utils/oversightFormat";
import { todayStats } from "./coachOverviewDerive";

/** One number in the strip. Values are null-safe: unknown reads "—", never 0. */
function Stat({ value, label, accent = false }) {
  return (
    <div className="flex-shrink-0">
      <p className={`text-[22px] leading-7 font-bold ${accent ? "text-orange-primary" : "text-gray-800"}`}>
        {value}
      </p>
      <p className="mt-0.5 text-[11px] uppercase tracking-wide text-gray-500 whitespace-nowrap">
        {label}
      </p>
    </div>
  );
}

const dash = (v) => (v == null ? "—" : v);

/**
 * The live band at the top of the Overview tab.
 *
 * Presence is live (websocket, ±60s); the rest is the newest bucket of the
 * analytics series. When the selected range does not include today the strip
 * relabels itself to the last day of the range rather than claiming to be
 * current — the numbers would otherwise silently mean something else.
 *
 * @param {Object} props
 * @param {Object|null|undefined} props.payload - The coach analytics response.
 */
export function TodayStrip({ payload }) {
  const s = todayStats(payload);
  const period = s.isToday ? "today" : "on the last day of the range";

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 overflow-x-auto">
      <div className="flex items-center gap-6 lg:gap-8 min-w-max">
        <div className="flex items-center gap-2 pr-6 lg:pr-8 border-r border-gray-100 flex-shrink-0">
          <span
            aria-hidden="true"
            className={`w-2 h-2 rounded-full ${
              s.isOnlineNow ? "bg-success animate-live-pulse" : "bg-gray-200"
            }`}
          />
          <div>
            <p className="text-[13px] font-bold text-gray-800">
              {s.isOnlineNow ? "Online now" : "Offline"}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-400">Presence is live</p>
          </div>
        </div>

        <Stat
          value={s.onlineSeconds == null ? "—" : formatDuration(s.onlineSeconds)}
          label={s.isToday ? "Online today" : "Online that day"}
        />
        <Stat value={dash(s.messages)} label={`Messages ${period === "today" ? "today" : "that day"}`} accent />
        <Stat
          value={
            s.roomsMessaged == null ? (
              "—"
            ) : (
              <>
                {s.roomsMessaged}
                {s.totalRooms != null && (
                  <span className="text-[13px] font-normal text-gray-400"> / {s.totalRooms}</span>
                )}
              </>
            )
          }
          label="Rooms they messaged in"
        />
        <Stat
          value={
            s.medianFirstResponseSeconds == null
              ? "—"
              : formatSecondsBrief(s.medianFirstResponseSeconds)
          }
          label="How fast they replied"
        />
        <Stat value={dash(s.sessions)} label="Sign-ins" />
      </div>

      {!s.isToday && (
        <p className="mt-3 text-[11px] text-gray-400">
          This range ends in the past, so these are the last day of the range — not today.
        </p>
      )}
    </div>
  );
}

export default TodayStrip;
